import { Controller } from '@hotwired/stimulus';
import debounce from 'lodash/debounce';
import { Turbo } from '@hotwired/turbo-rails';

const BTN_CLASS_DEFAULT = 'btn-tertiary';
const BTN_CLASS_ACTIVE = 'btn-primary';

export default class extends Controller {
  static targets = [
    'input',
    'spinner',
    'results',
    'icon',
    'sortCreatedAt',
    'sortName',
    'sortBirthday',
    'sortLabel1',
    'sortLabel2',
    'sortLabel3',
    'sortLabel4',
    'sortLabel5',
    'castQueryDropdown',
  ];

  static values = {
    url: String,
  };

  initialize() {
    this.update = debounce(this.update, 200).bind(this);
  }

  connect(event) {
    this.resetSortTargets();
    this.sortByCreatedAt(event, true);
  }

  input() {
    // Clear castQuery to avoid confusion
    this.castQueryReset();

    this.update();
  }

  async update() {
    this.resultsTarget.innerHTML = null;
    this.spinnerTarget.classList.remove('hidden');

    await fetch(this.queryUrl(this.inputTarget.value), {
      headers: { Accept: 'text/vnd.turbo-stream.html' },
    })
      .then((response) => response.text())
      .then((html) => {
        Turbo.renderStreamMessage(html);

        if (this.inputTarget.value.length > 0) {
          this.iconTarget.classList.remove('bg-white');
          this.iconTarget.classList.add('bg-gold');
        } else {
          this.iconTarget.classList.remove('bg-gold');
          this.iconTarget.classList.add('bg-white');
        }

        const compareController =
          this.application.getControllerForElementAndIdentifier(
            this.element,
            'compare',
          );
        if (compareController) compareController.refresh();
      });
  }

  sortByCreatedAt(_event, skipUpdate = false) {
    this.sortParam = 'order_by_created_at';
    if (!skipUpdate) this.update();

    this.resetSortTargets();
    this.sortCreatedAtTarget.classList.remove(BTN_CLASS_DEFAULT);
    this.sortCreatedAtTarget.classList.add(BTN_CLASS_ACTIVE);
  }

  sortByName() {
    this.sortParam = 'order_by_name';
    this.update();

    this.resetSortTargets();
    this.sortNameTarget.classList.remove(BTN_CLASS_DEFAULT);
    this.sortNameTarget.classList.add(BTN_CLASS_ACTIVE);
  }

  sortByBirthday() {
    this.sortParam = 'order_by_birthday';
    this.update();

    this.resetSortTargets();
    this.sortBirthdayTarget.classList.remove(BTN_CLASS_DEFAULT);
    this.sortBirthdayTarget.classList.add(BTN_CLASS_ACTIVE);
  }

  sortByLabel1() {
    this.sortParam = 'order_by_label1';
    this.update();

    this.resetSortTargets();
    this.sortLabel1Target.classList.remove(BTN_CLASS_DEFAULT);
    this.sortLabel1Target.classList.add(BTN_CLASS_ACTIVE);
  }

  sortByLabel2() {
    this.sortParam = 'order_by_label2';
    this.update();

    this.resetSortTargets();
    this.sortLabel2Target.classList.remove(BTN_CLASS_DEFAULT);
    this.sortLabel2Target.classList.add(BTN_CLASS_ACTIVE);
  }

  sortByLabel3() {
    this.sortParam = 'order_by_label3';
    this.update();

    this.resetSortTargets();
    this.sortLabel3Target.classList.remove(BTN_CLASS_DEFAULT);
    this.sortLabel3Target.classList.add(BTN_CLASS_ACTIVE);
  }

  sortByLabel4() {
    this.sortParam = 'order_by_label4';
    this.update();

    this.resetSortTargets();
    this.sortLabel4Target.classList.remove(BTN_CLASS_DEFAULT);
    this.sortLabel4Target.classList.add(BTN_CLASS_ACTIVE);
  }

  sortByLabel5() {
    this.sortParam = 'order_by_label5';
    this.update();

    this.resetSortTargets();
    this.sortLabel5Target.classList.remove(BTN_CLASS_DEFAULT);
    this.sortLabel5Target.classList.add(BTN_CLASS_ACTIVE);
  }

  resetSortTargets() {
    this.sortCreatedAtTarget.classList.remove(BTN_CLASS_ACTIVE);
    this.sortNameTarget.classList.remove(BTN_CLASS_ACTIVE);
    this.sortBirthdayTarget.classList.remove(BTN_CLASS_ACTIVE);
    this.sortLabel1Target.classList.remove(BTN_CLASS_ACTIVE);
    this.sortLabel2Target.classList.remove(BTN_CLASS_ACTIVE);
    this.sortLabel3Target.classList.remove(BTN_CLASS_ACTIVE);
    this.sortLabel4Target.classList.remove(BTN_CLASS_ACTIVE);
    this.sortLabel5Target.classList.remove(BTN_CLASS_ACTIVE);

    this.sortCreatedAtTarget.classList.add(BTN_CLASS_DEFAULT);
    this.sortNameTarget.classList.add(BTN_CLASS_DEFAULT);
    this.sortBirthdayTarget.classList.add(BTN_CLASS_DEFAULT);
    this.sortLabel1Target.classList.add(BTN_CLASS_DEFAULT);
    this.sortLabel2Target.classList.add(BTN_CLASS_DEFAULT);
    this.sortLabel3Target.classList.add(BTN_CLASS_DEFAULT);
    this.sortLabel4Target.classList.add(BTN_CLASS_DEFAULT);
    this.sortLabel5Target.classList.add(BTN_CLASS_DEFAULT);
  }

  queryUrl(query) {
    const url = new URL(this.urlValue, window.location.origin);
    url.searchParams.set('query', query);
    console.log('Current castQuery state:', this.castQuery);

    if (this.sortParam) {
      url.searchParams.set(this.sortParam, 'asc');
    }

    if (this.castQuery) {
      // Only include parameters for the currently selected criterion
      const currentCriterion = this.castQuery.criterion;

      if (currentCriterion === 'stemAndBranch') {
        const key = 'cast_query[stem_and_branch][]';

        // Use append for stem (if it exists)
        if (this.castQuery['stemAndBranch[stem]']) {
          url.searchParams.append(key, this.castQuery['stemAndBranch[stem]']);
        }

        // Use append for branch (if it exists)
        if (this.castQuery['stemAndBranch[branch]']) {
          url.searchParams.append(key, this.castQuery['stemAndBranch[branch]']);
        }

        // Handle multi-select pillar values
        const pillarValues = this.castQuery['stemAndBranch[pillar][]'];
        if (Array.isArray(pillarValues)) {
          pillarValues.forEach((value) => {
            url.searchParams.append(key, value);
          });
        } else if (pillarValues) {
          url.searchParams.append(key, pillarValues);
        }
      } else if (currentCriterion === 'baziStructure') {
        url.searchParams.set(
          'cast_query[bazi_structure]',
          this.castQuery.baziStructure,
        );
      } else if (currentCriterion === 'generalMentalPattern') {
        url.searchParams.set(
          'cast_query[general_mental_pattern]',
          this.castQuery.generalMentalPattern,
        );
      } else if (currentCriterion === 'individualMentalPattern') {
        url.searchParams.set(
          'cast_query[individual_mental_pattern]',
          this.castQuery.individualMentalPattern,
        );
      } else if (currentCriterion === 'yongShenMentalPattern') {
        url.searchParams.set(
          'cast_query[yong_shen_mental_pattern]',
          this.castQuery.yongShenMentalPattern,
        );
      } else if (currentCriterion === 'symbolicStar') {
        const key = 'cast_query[symbolic_star][]';
        url.searchParams.append(key, this.castQuery['symbolicStar[star]']);

        // Handle multi-select pillar values for symbolic star
        const pillarValues = this.castQuery['symbolicStar[pillar][]'];
        if (Array.isArray(pillarValues)) {
          pillarValues.forEach((value) => {
            url.searchParams.append(key, value);
          });
        } else if (pillarValues) {
          url.searchParams.append(key, pillarValues);
        }
      } else if (currentCriterion === 'interaction') {
        const key = 'cast_query[interaction][]';
        url.searchParams.append(
          key,
          this.castQuery['interaction[aspect_type]'],
        );

        // Handle multi-select pillar values for interaction
        const pillarValues = this.castQuery['interaction[pillar][]'];
        if (Array.isArray(pillarValues)) {
          pillarValues.forEach((value) => {
            url.searchParams.append(key, value);
          });
        } else if (pillarValues) {
          url.searchParams.append(key, pillarValues);
        }
      }
    } else {
      url.searchParams.set('cast_query', '');
    }

    return url.toString();
  }

  castQueryUpdate(e) {
    e.preventDefault();
    console.log('CAST QUERY Before');
    console.log(this.castQuery);
    // Reset the previous castQuery before processing the new form
    this.castQuery = null;

    const formData = new FormData(e.target);

    const formProps = {};
    for (const [key, value] of formData.entries()) {
      if (formProps[key]) {
        if (!Array.isArray(formProps[key])) {
          formProps[key] = [formProps[key]];
        }
        formProps[key].push(value);
      } else {
        formProps[key] = value;
      }
    }
    console.log(`FormProps:`);
    console.log(formProps);
    this.castQuery = formProps;

    // Clear text input to avoid confusion
    this.inputTarget.value = null;

    this.update();

    this.castQueryDropdownTarget.classList.toggle('btn-active', true);
  }

  castQueryReset() {
    this.castQuery = null;
    this.update();

    this.castQueryDropdownTarget.classList.toggle('btn-active', false);
  }
}
